import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/apiAuth';
import config from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [baseCampUserAgreementTerms, setBaseCampUserAgreementTerms] = useState(null);
    const [authReady, setAuthReady] = useState(!localStorage.getItem('token'));

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }, [token]);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setBaseCampUserAgreementTerms(null);
        setAuthReady(true);
    }, []);

    useEffect(() => {
        if (!token) {
            setUser(null);
            setBaseCampUserAgreementTerms(null);
            setAuthReady(true);
            return;
        }

        let cancelled = false;
        const loadMe = async () => {
            try {
                const data = await fetchWithAuth(`${config.getApiUrl()}/api/auth/me`);
                if (!cancelled) {
                    setUser(data.user);
                    setBaseCampUserAgreementTerms(data.baseCampUserAgreementTerms ?? null);
                    setAuthReady(true);
                }
                return data;
            } catch (error) {
                if (!cancelled) {
                    logout();
                }
                throw error;
            }
        };

        loadMe();
        return () => {
            cancelled = true;
        };
    }, [token, logout]);

    const login = async (username, password) => {
        try {
            const data = await fetchWithAuth(`${config.getApiUrl()}/api/auth/login`, {
                method: 'POST',
                body: JSON.stringify({ username, password })
            });

            if (data.token) {
                setUser(data.user);
                setToken(data.token);
                setAuthReady(true);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Login error:', error);
            return false;
        }
    };

    const refreshUser = useCallback(async () => {
        if (!token) {
            setUser(null);
            return null;
        }
        const data = await fetchWithAuth(`${config.getApiUrl()}/api/auth/me`);
        setUser(data.user);
        setBaseCampUserAgreementTerms(data.baseCampUserAgreementTerms ?? null);
        return data;
    }, [token]);

    return (
        <AuthContext.Provider value={{
            token,
            user,
            login,
            logout,
            authReady,
            refreshUser,
            baseCampUserAgreementTerms
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
